<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import axios from 'axios';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import { PowerBIReportEmbed } from 'powerbi-client-vue-js';
    import { usePowerBiData } from '@/composables/usePowerBiData';
    import { toast } from 'vue-sonner';
    import PowerBIOriginal from '@/components/icons/PowerBIOriginal.vue';
    import { useI18n } from 'vue-i18n';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
    const { t } = useI18n();

    const {
        powerBiData,
        isPowerBiPending,
        isPowerBiFetching,
        isPowerBiSuccess,
        isPowerBiDataError,
        powerBiDataError,
    } = usePowerBiData(selectedCompanyId.value);

    watch(powerBiDataError, (error: any) => {
        if (error) {
            toast.error(error?.response?.data?.error || t('power_bi.error_fetching_power_bi_data'));
        }
    });

    const embedUrl = computed(() => {
        return `https://app.powerbi.com/reportEmbed?reportId=${powerBiData.value.report_id}&groupId=${powerBiData.value.group_id}`;
    });
</script>

<template>
    <PowerBIReportEmbed
        v-if="powerBiData && powerBiData.report_id"
        :embedConfig="{
            type: 'report',
            id: powerBiData.report_id,
            embedUrl: embedUrl,
            accessToken: powerBiData.access_token,
            tokenType: 1,
            settings: {
                panes: {
                    filters: {
                        expanded: false,
                        visible: false,
                    },
                },
                background: 0,
            },
        }"
        cssClassName="powerbi-embed"
        :phasedEmbedding="false"
        :eventHandlers="
            new Map([
                ['loaded', () => console.log('Report loaded')],
                ['rendered', () => console.log('Report rendered')],
                ['error', (event: any) => console.log(event)],
            ])
        "
    />
    <div
        v-else-if="!isPowerBiFetching"
        class="flex h-full w-full flex-1 flex-col items-center justify-center"
    >
        <PowerBIOriginal className="size-12 mb-4" />
        <h1 class="text-xl font-bold">
            {{ $t('power_bi.no_power_bi_data_found') }}
        </h1>
        <p class="text-muted-foreground">
            {{ $t('power_bi.please_contact_support') }}
        </p>
    </div>
</template>

<style>
    .powerbi-embed iframe {
        aspect-ratio: 16 / 7;
    }
</style>
